import { Suspense } from 'react';
import type { PropsWithChildren, ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import { BaseViewerDataProvider } from 'domains/viewer/Viewer/StudyLoader/useStudies';
import { CustomWorklistsQueryProvider } from 'domains/worklist/Worklist/hooks/customWorklistsContextedQuery';
import { useWorklistAutoloadState } from 'domains/worklist/Worklist/hooks/useWorklistAutoloadState';
import { ToolPreferencesQueryProvider } from 'hooks/usePreferences';
import { useIsAuthenticated } from 'hooks/useAuth';
import { useCheckUserRISPermissions } from 'integrations/RIS/checkPermissions';
import type { WithApolloContext } from 'modules/Apollo/types';
import { PageTypes, getPageType } from 'utils/pageTypes';
import { isLogoutPathname } from 'utils/router';

type AppQueryProvidersProps = PropsWithChildren<WithApolloContext>;

const WorklistAndViewerQueryProviders = ({
  children,
  context,
}: AppQueryProvidersProps): ReactElement => {
  const location = useLocation();
  const { canAccessWorklist } = useCheckUserRISPermissions();
  const { isAutoloadEnabled } = useWorklistAutoloadState();
  const pageType = getPageType(location.pathname);
  const isWorklistSurface =
    pageType === PageTypes.WORKLIST || pageType === PageTypes.PATIENT_JACKET;
  const isAutoloadSurface = pageType === PageTypes.REPORTER || pageType === PageTypes.VIEWER;
  const shouldSkipCustomWorklists =
    !canAccessWorklist || (!isWorklistSurface && !(isAutoloadSurface && isAutoloadEnabled));

  return (
    <Suspense>
      <CustomWorklistsQueryProvider context={context} skip={shouldSkipCustomWorklists}>
        <BaseViewerDataProvider context={context}>{children}</BaseViewerDataProvider>
      </CustomWorklistsQueryProvider>
    </Suspense>
  );
};

/**
 * Keeps cross-domain query ownership stable across route transitions. These providers intentionally
 * live above StrictMode; their queries skip when authentication, permissions, or route state make
 * them irrelevant.
 */
export const AppQueryProviders = ({ children, context }: AppQueryProvidersProps): ReactElement => {
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const shouldRunAuthenticatedQueries = isAuthenticated && !isLogoutPathname(location.pathname);

  return (
    <ToolPreferencesQueryProvider options={{ context, skip: !shouldRunAuthenticatedQueries }}>
      {shouldRunAuthenticatedQueries ? (
        <WorklistAndViewerQueryProviders context={context}>
          {children}
        </WorklistAndViewerQueryProviders>
      ) : (
        children
      )}
    </ToolPreferencesQueryProvider>
  );
};
